/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Receipt, CreditCard, Landmark, Wallet, Loader2 } from 'lucide-react';
import { StoreFilter } from '@/components/StoreFilter';
import { getPsp } from '@/lib/api';
import { PaymentStatus } from '@/components/admin/PaymentStatus';
import { formatMoney } from '@/lib/adminFormat';

// Maps the Stripe/Viva payment method type → label, icon and colour.
const PM_META = {
  card: { label: 'Carte', icon: CreditCard, cls: 'bg-zinc-50 border-zinc-200 text-zinc-700' },
  klarna: { label: 'Klarna', icon: Wallet, cls: 'bg-pink-50 border-pink-200 text-pink-700' },
  ideal: { label: 'iDEAL', icon: Landmark, cls: 'bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700' },
  bancontact: { label: 'Bancontact', icon: Landmark, cls: 'bg-blue-50 border-blue-200 text-blue-700' },
  sepa_debit: { label: 'SEPA', icon: Landmark, cls: 'bg-indigo-50 border-indigo-200 text-indigo-700' },
  paypal: { label: 'PayPal', icon: Wallet, cls: 'bg-sky-50 border-sky-200 text-sky-700' },
  link: { label: 'Link', icon: Wallet, cls: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  giropay: { label: 'Giropay', icon: Landmark, cls: 'bg-orange-50 border-orange-200 text-orange-700' },
  eps: { label: 'EPS', icon: Landmark, cls: 'bg-zinc-50 border-zinc-200 text-zinc-700' },
};
const CARD_BRAND = { visa: 'Visa', mastercard: 'Mastercard', amex: 'Amex', discover: 'Discover',
  diners: 'Diners', jcb: 'JCB', unionpay: 'UnionPay' };

const PaymentMethodBadge = ({ method, brand, provider, testId }) => {
  if (!method) return <span className="text-xs text-zinc-400" data-testid={testId}>—</span>;
  const meta = PM_META[method] || { label: method, icon: CreditCard, cls: 'bg-zinc-50 border-zinc-200 text-zinc-700' };
  const Icon = meta.icon;
  const label = method === 'card' && brand ? (CARD_BRAND[brand] || 'Carte') : meta.label;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-md border ${meta.cls}`}
          title={provider === 'viva' ? 'Via Viva Wallet' : provider === 'payzen' ? 'Via PayZen' : 'Via Stripe'} data-testid={testId}>
      <Icon className="h-3.5 w-3.5" /> {label}
    </span>
  );
};

const itemName = (i) => i.selected_name || i.vitrine_name || i.shopify_name || 'Produit';
const itemsSummary = (t) => (t.items || [])
  .map((i) => `${i.quantity}× ${itemName(i)}${i.variant_title ? ` (${i.variant_title})` : ''}`)
  .join(', ');

export default function Transactions() {
  const [tx, setTx] = useState([]);
  const [selected, setSelected] = useState(null);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(''); // '' = toutes les boutiques
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => { api.get('/stores').then((r) => setStores(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    let active = true;
    setLoading(true); setError(false);
    const params = { limit: 500 };
    if (storeId) params.store_id = storeId;
    api.get('/transactions', { params }).then((r) => { if (active) setTx(r.data); }).catch(() => { if (active) { setTx([]); setError(true); } }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [storeId, refresh]);

  const psp = getPsp();
  const pspLabel = psp === 'viva' ? 'Viva Wallet' : psp === 'payzen' ? 'PayZen' : 'Stripe';
  const selStore = stores.find((s) => s.id === storeId);
  const platform = selStore?.platform;
  const orderLabel = platform === 'woocommerce' ? 'Commande WooCommerce'
    : platform === 'shopify' ? 'Commande Shopify' : 'Commande';

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-7xl" data-testid="transactions-page">
      <div className="mb-8">
        <div className="dashboard-eyebrow">Journal</div>
        <h1 data-testid="transactions-title">Transactions</h1>
        <p className="dashboard-caption" data-testid="transactions-provider">Paiements {pspLabel}</p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="transactions-filter-bar">
        <StoreFilter stores={stores} value={storeId} onChange={setStoreId} testId="transactions-store-filter" />
        <span className="text-xs text-zinc-500" data-testid="transactions-count">{loading ? '—' : tx.length} transaction{tx.length !== 1 ? 's' : ''}</span>
      </div>
      {loading ? <div className="dashboard-loading" role="status" data-testid="transactions-loading"><Loader2 className="h-4 w-4 animate-spin" />Chargement des transactions…</div> : error ? <div className="dashboard-feedback" role="alert" data-testid="transactions-error"><span>Impossible de charger les transactions.</span><button type="button" onClick={() => setRefresh((v) => v + 1)} data-testid="transactions-retry" className="underline">Réessayer</button></div> : <Card className="card-flat admin-ledger overflow-hidden">
        {/* Desktop / tablet: table */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <th className="px-6 py-3 overline font-medium">Date</th>
                <th className="px-6 py-3 overline font-medium">Client</th>
                <th className="px-6 py-3 overline font-medium text-right">Montant</th>
                <th className="px-6 py-3 overline font-medium">Moyen</th>
                <th className="px-6 py-3 overline font-medium">Paiement</th>
                <th className="px-6 py-3 overline font-medium">{orderLabel}</th>
                <th className="px-6 py-3 overline font-medium">Session</th>
              </tr>
            </thead>
            <tbody>
              {tx.length === 0 ? (
                <tr><td colSpan={7} className="px-6 py-12 text-center text-zinc-500" data-testid="transactions-empty">
                  <Receipt className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
                  Aucune transaction
                </td></tr>
              ) : tx.map((t) => (
                <tr
                  key={t.id}
                  onClick={() => setSelected(t)}
                  data-testid={`tx-row-${t.id}`}
                  tabIndex={0}
                  aria-label={`Détails de la transaction ${t.customer?.email || t.id}`}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelected(t); } }}
                  className="border-b border-zinc-100 hover:bg-zinc-50 cursor-pointer"
                >
                  <td className="px-6 py-3 font-mono text-xs">{new Date(t.created_at).toLocaleString('fr-FR')}</td>
                  <td className="px-6 py-3 max-w-[240px]">
                    <div className="truncate">{t.customer?.email || '—'}</div>
                    {itemsSummary(t) && (
                      <div className="text-[11px] text-zinc-400 truncate" data-testid={`tx-items-summary-${t.id}`}>{itemsSummary(t)}</div>
                    )}
                  </td>
                  <td data-testid={`tx-amount-${t.id}`} className="px-6 py-3 font-mono font-medium text-right whitespace-nowrap">{formatMoney(t.amount, t.currency)}</td>
                  <td className="px-6 py-3"><PaymentMethodBadge testId={`tx-method-${t.id}`} method={t.payment_method} brand={t.card_brand} provider={t.provider} /></td>
                  <td className="px-6 py-3"><PaymentStatus testId={`tx-status-${t.id}`} status={t.payment_status} /></td>
                  <td className="px-6 py-3 font-mono text-xs">
                    {t.shopify_order_name || (t.shopify_error ? <span className="text-amber-600">⚠ erreur</span> : '—')}
                  </td>
                  <td className="px-6 py-3 font-mono text-xs text-zinc-500 truncate max-w-[120px]">{t.session_id?.slice(0,18)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: stacked cards */}
        <div className="sm:hidden divide-y divide-zinc-100" data-testid="transactions-mobile-list">
          {tx.length === 0 ? (
            <div className="px-4 py-12 text-center text-zinc-500">
              <Receipt className="h-10 w-10 mx-auto text-zinc-300 mb-3" />
              Aucune transaction
            </div>
          ) : tx.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelected(t)}
              data-testid={`tx-card-${t.id}`}
              className="w-full text-left px-4 py-3.5 active:bg-zinc-50"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{t.customer?.email || '—'}</div>
                  <div className="text-[11px] text-zinc-400 font-mono mt-0.5">{new Date(t.created_at).toLocaleString('fr-FR')}</div>
                </div>
                <div data-testid={`tx-mobile-amount-${t.id}`} className="font-mono font-semibold shrink-0">{formatMoney(t.amount, t.currency)}</div>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                <PaymentStatus testId={`tx-mobile-status-${t.id}`} status={t.payment_status} />
                <PaymentMethodBadge testId={`tx-mobile-method-${t.id}`} method={t.payment_method} brand={t.card_brand} provider={t.provider} />
                {t.shopify_order_name && <span className="text-[11px] font-mono text-zinc-500">{t.shopify_order_name}</span>}
                {!t.shopify_order_name && t.shopify_error && <span className="text-[11px] text-amber-600">⚠ erreur</span>}
              </div>
              {itemsSummary(t) && (
                <div className="mt-1.5 text-[11px] text-zinc-400 truncate">{itemsSummary(t)}</div>
              )}
            </button>
          ))}
        </div>
      </Card>}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl" data-testid="transaction-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Détails de la transaction</DialogTitle>
            <DialogDescription className="sr-only">Informations du client, articles et paiement.</DialogDescription>
          </DialogHeader>
          {selected && (
            <div className="space-y-4 text-sm max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><div className="overline mb-1">ID</div><div className="font-mono text-xs break-all">{selected.id}</div></div>
                <div><div className="overline mb-1">{selected.provider === 'viva' ? 'Référence Viva' : selected.provider === 'payzen' ? 'Référence PayZen' : 'Session Stripe'}</div><div className="font-mono text-xs break-all">{selected.session_id}</div></div>
                <div><div className="overline mb-1">Montant</div><div className="font-mono font-bold" data-testid="tx-detail-amount">{formatMoney(selected.amount, selected.currency)}</div></div>
                <div><div className="overline mb-1">Statut</div><PaymentStatus testId="tx-detail-status" status={selected.payment_status} /></div>
                <div><div className="overline mb-1">Moyen de paiement</div><PaymentMethodBadge testId="tx-detail-method" method={selected.payment_method} brand={selected.card_brand} provider={selected.provider} /></div>
              </div>
              <div className="dashed-divider pt-4">
                <div className="overline mb-2">Client</div>
                <div className="font-mono text-xs space-y-1">
                  <div>{selected.customer?.first_name} {selected.customer?.last_name}</div>
                  <div>{selected.customer?.email}</div>
                  <div>{selected.customer?.address1}, {selected.customer?.postal_code} {selected.customer?.city}, {selected.customer?.country}</div>
                  {selected.customer?.phone && <div>{selected.customer.phone}</div>}
                </div>
              </div>
              <div className="dashed-divider pt-4">
                <div className="overline mb-2">Articles commandés</div>
                <div className="space-y-2" data-testid="tx-detail-items">
                  {(selected.items || []).length === 0 ? (
                    <div className="text-xs text-zinc-400">Aucun article enregistré.</div>
                  ) : selected.items.map((i, idx) => (
                    <div key={idx} className="flex justify-between text-xs gap-3 items-start">
                      <span className="min-w-0">
                        <span className="font-medium text-zinc-800">{itemName(i)}</span>
                        {i.variant_title && <span className="text-zinc-500"> — {i.variant_title}</span>}
                        <span className="font-mono text-zinc-400"> ×{i.quantity}</span>
                        {i.bogo_discount > 0 && (
                          <span className="text-emerald-600 ml-1 font-medium">−{formatMoney(i.bogo_discount, selected.currency)}</span>
                        )}
                        {i.sku && <span className="block text-[10px] font-mono text-zinc-400">{i.sku}</span>}
                      </span>
                      <span data-testid={`tx-detail-item-amount-${idx}`} className="font-mono shrink-0 text-zinc-700">{formatMoney(i.price * i.quantity, selected.currency)}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1 text-xs font-mono">
                  {selected.vitrine_metadata?.subtotal != null && (
                    <div className="flex justify-between text-zinc-500"><span>Sous-total</span><span data-testid="tx-detail-subtotal">{formatMoney(selected.vitrine_metadata.subtotal, selected.currency)}</span></div>
                  )}
                  {selected.vitrine_metadata?.bogo_discount > 0 && (
                    <div className="flex justify-between text-emerald-600"><span>Remise 2ème article</span><span data-testid="tx-detail-discount">−{formatMoney(selected.vitrine_metadata.bogo_discount, selected.currency)}</span></div>
                  )}
                  {selected.vitrine_metadata?.shipping != null && (
                    <div className="flex justify-between text-zinc-500"><span>Livraison{selected.vitrine_metadata?.shipping_name ? ` · ${selected.vitrine_metadata.shipping_name}` : ''}</span><span data-testid="tx-detail-shipping">{formatMoney(selected.vitrine_metadata.shipping, selected.currency)}</span></div>
                  )}
                  <div className="flex justify-between font-bold text-zinc-900 pt-1"><span>Total payé</span><span data-testid="tx-detail-total">{formatMoney(selected.amount, selected.currency)}</span></div>
                </div>
              </div>
                <div className="dashed-divider pt-4">
                  <div className="overline mb-1">{(stores.find((s) => s.id === selected.store_id)?.platform) === 'woocommerce' ? 'Commande WooCommerce' : 'Commande Shopify'}</div>
                  <div className="font-mono">{selected.shopify_order_name} ({selected.shopify_order_id})</div>
                </div>
              )}
              {selected.shopify_error && (
                <div className="bg-amber-50 border border-amber-200 p-3 rounded-md text-xs text-amber-800">
                  <strong>Erreur {(stores.find((s) => s.id === selected.store_id)?.platform) === 'woocommerce' ? 'WooCommerce' : 'Shopify'} :</strong> {selected.shopify_error}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
